import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from './config';

const GapAnalysis = () => {
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [selectedGap, setSelectedGap] = useState(null);
  const [viewMode, setViewMode] = useState('gaps'); // 'gaps' or 'roadmap'
  const navigate = useNavigate();

  useEffect(() => {
    fetchGapAnalysis();
  }, []);

  const fetchGapAnalysis = async () => {
    try {
      setLoading(true);
      
      // Get customer ID and assessment ID from session/context
      const customerId = JSON.parse(localStorage.getItem('ciso_user') || '{}').customer_id || '';
      const assessmentId = localStorage.getItem('current_assessment_id');
      
      if (!customerId || !assessmentId) {
        setError('No assessment found. Please complete an assessment first.');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${config.apiUrl}/api/gap-analysis/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            assessment_id: parseInt(assessmentId),
            customer_id: customerId
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to generate gap analysis');
      }

      const data = await response.json();
      setAnalysis(data);
      
      // Auto-select first gap
      if (data.gaps && data.gaps.length > 0) {
        setSelectedGap(data.gaps[0]);
      }
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Critical': 'bg-red-100 text-red-800 border-red-300',
      'High': 'bg-orange-100 text-orange-800 border-orange-300',
      'Medium': 'bg-yellow-100 text-yellow-800 border-yellow-300',
      'Low': 'bg-blue-100 text-blue-800 border-blue-300'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Critical': 'bg-red-500',
      'High': 'bg-orange-500',
      'Medium': 'bg-yellow-500',
      'Low': 'bg-blue-500'
    };
    return colors[priority] || 'bg-gray-500';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatHours = (hours) => {
    const weeks = Math.ceil(hours / 40);
    return `${hours} hours (~${weeks} ${weeks === 1 ? 'week' : 'weeks'})`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-slate-600">Generating gap analysis...</div>
          <div className="text-sm text-slate-500 mt-2">Analyzing assessment data and personalizing recommendations</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 max-w-md">
          <div className="text-red-600 text-lg font-semibold mb-2">Error</div>
          <div className="text-slate-600 mb-4">{error}</div>
          <button
            onClick={() => navigate('/ciso-dashboard')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (!analysis || !analysis.gaps || analysis.gaps.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 max-w-md text-center">
          <div className="text-6xl mb-4">🎉</div>
          <div className="text-xl font-semibold text-slate-800 mb-2">
            No Gaps Found!
          </div>
          <div className="text-slate-600 mb-6">
            Your current maturity levels meet or exceed your targets across all security categories.
          </div>
          <button
            onClick={() => navigate('/ciso-dashboard')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Gap Analysis & Recommendations</h1>
              <p className="text-slate-600 mt-1">
                Personalized remediation plan based on your security assessment
              </p>
            </div>
            <button
              onClick={() => navigate('/ciso-dashboard')}
              className="text-slate-600 hover:text-slate-900"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
            <div className="text-sm text-slate-600 mb-1">Overall Gap Score</div>
            <div className="text-3xl font-bold text-slate-900">{analysis.overall_gap_score}</div>
            <div className="text-xs text-slate-500 mt-1">Average maturity gap</div>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
            <div className="text-sm text-slate-600 mb-1">Total Gaps</div>
            <div className="text-3xl font-bold text-slate-900">{analysis.gaps.length}</div>
            <div className="text-xs text-slate-500 mt-1">Categories to improve</div>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
            <div className="text-sm text-slate-600 mb-1">Estimated Effort</div>
            <div className="text-2xl font-bold text-slate-900">{formatHours(analysis.total_effort_hours)}</div>
            <div className="text-xs text-slate-500 mt-1">To close all gaps</div>
          </div>
          
          <div className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm">
            <div className="text-sm text-slate-600 mb-1">Estimated Cost</div>
            <div className="text-2xl font-bold text-slate-900">{formatCurrency(analysis.total_estimated_cost)}</div>
            <div className="text-xs text-slate-500 mt-1">Professional services</div>
          </div>
        </div>

        {/* View Mode Toggle */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setViewMode('gaps')}
            className={`px-4 py-2 rounded font-medium ${
              viewMode === 'gaps'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            Gap Details
          </button>
          <button
            onClick={() => setViewMode('roadmap')}
            className={`px-4 py-2 rounded font-medium ${
              viewMode === 'roadmap'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            Implementation Roadmap
          </button>
        </div>

        {/* Content Area */}
        {viewMode === 'gaps' ? (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Gap List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-4 border-b border-slate-200 bg-slate-50">
                  <h2 className="font-semibold text-slate-900">Security Gaps ({analysis.gaps.length})</h2>
                  <p className="text-xs text-slate-600 mt-1">Sorted by priority</p>
                </div>
                <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
                  {analysis.gaps.map((gap, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedGap(gap)}
                      className={`w-full text-left p-4 hover:bg-slate-50 transition-colors ${
                        selectedGap?.category === gap.category ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="font-medium text-slate-900 text-sm">{gap.category}</div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityBadge(gap.priority)} text-white`}>
                          {gap.priority}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-600">
                        <span>L{gap.current_level} → L{gap.target_level}</span>
                        <span className="text-slate-400">•</span>
                        <span>Gap: {gap.gap_size}</span>
                      </div>
                      <div className="mt-2 text-xs text-slate-500">
                        {formatHours(gap.effort_hours)} • {formatCurrency(gap.effort_cost)}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Gap Details */}
            <div className="lg:col-span-2">
              {selectedGap && (
                <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
                  <div className={`p-6 border-b border-slate-200 ${getPriorityColor(selectedGap.priority)}`}>
                    <div className="flex items-start justify-between mb-2">
                      <h2 className="text-xl font-bold">{selectedGap.category}</h2>
                      <span className={`px-3 py-1 rounded font-medium text-sm ${getPriorityBadge(selectedGap.priority)} text-white`}>
                        {selectedGap.priority} Priority
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      <div>
                        <span className="font-medium">Current:</span> Level {selectedGap.current_level}
                      </div>
                      <div>
                        <span className="font-medium">Target:</span> Level {selectedGap.target_level}
                      </div>
                      <div>
                        <span className="font-medium">Gap:</span> {selectedGap.gap_size} {selectedGap.gap_size === 1 ? 'level' : 'levels'}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-6">
                    {/* Effort & Cost */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="text-sm text-slate-600 mb-1">Estimated Effort</div>
                        <div className="text-lg font-bold text-slate-900">{formatHours(selectedGap.effort_hours)}</div>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-4">
                        <div className="text-sm text-slate-600 mb-1">Estimated Cost</div>
                        <div className="text-lg font-bold text-slate-900">{formatCurrency(selectedGap.effort_cost)}</div>
                      </div>
                    </div>

                    {/* Recommendations */}
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                        <span className="text-blue-600">📋</span>
                        Recommended Actions
                      </h3>
                      <ul className="space-y-2">
                        {selectedGap.recommendations.map((rec, idx) => (
                          <li key={idx} className="flex items-start gap-3 text-sm text-slate-700">
                            <span className="text-blue-600 font-bold mt-0.5">{idx + 1}.</span>
                            <span>{rec}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* AI Insights */}
                    {selectedGap.ai_insights && (
                      <div className="border-t border-slate-200 pt-6">
                        <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                          <span className="text-purple-600">🤖</span>
                          AI-Personalized Insights
                        </h3>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                          <pre className="text-sm text-slate-700 whitespace-pre-wrap font-sans">
                            {selectedGap.ai_insights}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Implementation Roadmap */
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
            <div className="p-6 border-b border-slate-200 bg-slate-50">
              <h2 className="text-xl font-bold text-slate-900">Implementation Roadmap</h2>
              <p className="text-slate-600 mt-1">Sequenced plan to close all security gaps</p>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {analysis.implementation_roadmap.map((phase, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-slate-900">{phase.phase}</h3>
                        <p className="text-slate-600 mt-1">{phase.description}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-slate-600">Duration</div>
                        <div className="text-xl font-bold text-blue-600">{phase.duration_weeks} weeks</div>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div className="bg-slate-50 rounded p-3">
                        <div className="text-xs text-slate-600 mb-1">Total Effort</div>
                        <div className="text-lg font-semibold text-slate-900">
                          {formatHours(phase.total_effort_hours)}
                        </div>
                      </div>
                      <div className="bg-slate-50 rounded p-3">
                        <div className="text-xs text-slate-600 mb-1">Categories</div>
                        <div className="text-lg font-semibold text-slate-900">
                          {phase.categories.length} areas
                        </div>
                      </div>
                    </div>

                    <div>
                      <div className="text-sm font-medium text-slate-700 mb-2">Categories in this phase:</div>
                      <div className="flex flex-wrap gap-2">
                        {phase.categories.map((cat, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium"
                          >
                            {cat}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">💡</span>
                  <div>
                    <div className="font-semibold text-blue-900 mb-1">Implementation Note</div>
                    <div className="text-sm text-blue-800">
                      This roadmap provides a suggested sequence for closing security gaps. Actual timeline may vary based on 
                      available resources, business priorities, and regulatory deadlines. Consider running phases in parallel 
                      where resources permit to accelerate maturity improvement.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GapAnalysis;
